// src/components/padrekko/qc/QcPicker.jsx
//
// Day 1 multi-party picker. Two views toggled by `mode`:
//
//   "list"     (default) — "+ New case" button + list of cases visible to
//                          the current org (owned + participating).
//   "newcase"            — KMZ upload, case name, autodetect toggle,
//                          customer + operator participant pickers, Submit.
//
// Clicking a case row calls useQcStore.loadCase(id) → ReclamationQcTab
// re-renders the inspector. Submitting the new-case form calls createCase
// which also lands directly in the inspector on success.

import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  Upload, Plus, Loader2, ChevronRight, ChevronLeft, FolderOpen, Info, ClipboardList,
  MapPin, Pencil,
} from "lucide-react";
// The card row is 9px and truncates, so it uses the SHORT set; the two org
// pickers below use the full CASE_ROLE_LABEL. Both come from roleLabels.js —
// this file previously carried a third vocabulary inline ("DH"/"Customer"/
// "Operator") that agreed with nothing.
import { CASE_ROLE_LABEL, CASE_ROLE_SHORT } from "../../../../../admin-api/lib/padrekko-qc/roleLabels.js";
import { useQcStore } from "../../../stores/useQcStore";
import { useCaseBuilderStore } from "../../../stores/useCaseBuilderStore";
import { fetchWithAuth } from "../../../api/fetchWithAuth";
import { useEffectiveOrg } from "../../../hooks/useEffectiveOrg";
import { useProjectStore } from "../../../stores/useProjectStore";
import ObligationsReport from "./ObligationsReport";

// Stage badge palette — mirrors what QcInspector's case header banner uses.
// dh = teal, customer = amber, operator = purple, complete = emerald.
const STAGE_TONES = {
  dh_review:       "bg-teal-500/15    border-teal-500/40    text-teal-200",
  customer_review: "bg-amber-500/15   border-amber-500/40   text-amber-200",
  operator_review: "bg-purple-500/15  border-purple-500/40  text-purple-200",
  complete:        "bg-emerald-500/15 border-emerald-500/40 text-emerald-200",
};
const STAGE_LABELS = {
  dh_review:       "DH review",
  customer_review: "Customer review",
  operator_review: "Operator review",
  complete:        "Complete",
};

// Tiny relative-time helper — same shape as the previous picker. No date
// library installed; good enough for v0.
function timeAgo(iso) {
  if (!iso) return "";
  const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60)     return "just now";
  if (sec < 3600)   return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400)  return `${Math.floor(sec / 3600)}h ago`;
  if (sec < 172800) return "yesterday";
  if (sec < 604800) return `${Math.floor(sec / 86400)}d ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric", month: "short", day: "numeric",
  });
}

// File → base64 (no `data:` prefix).
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export default function QcPicker() {
  const loading           = useQcStore((s) => s.loading);
  const error             = useQcStore((s) => s.error);
  const casesList         = useQcStore((s) => s.casesList);
  const casesListLoading  = useQcStore((s) => s.casesListLoading);
  const casesListError    = useQcStore((s) => s.casesListError);
  const createCase        = useQcStore((s) => s.createCase);
  const loadCase          = useQcStore((s) => s.loadCase);
  const listCases         = useQcStore((s) => s.listCases);

  const [mode, setMode] = useState("list"); // "list" | "newcase"
  const [obligationsOpen, setObligationsOpen] = useState(false);

  // Case creation is restricted to service-provider orgs (e.g., DH).
  // Customer / Operator participants see the case list but not the
  // "+ New case" button. The backend enforces the same gate in
  // POST /cases as defense in depth.
  const isServiceProvider = useEffectiveOrg()?.is_service_provider === true;

  // Filter the case list by the TopNav project picker. "global" (or no
  // selection) shows all the org's cases; a concrete project narrows to it.
  const activeProjectId = useProjectStore((s) => s.activeProjectId);
  useEffect(() => {
    listCases({ limit: 20, project: activeProjectId });
  }, [listCases, activeProjectId]);

  if (mode === "newcase") {
    return (
      <NewCaseForm
        source="kmz"
        onCancel={() => setMode("list")}
        loading={loading}
        error={error}
        createCase={createCase}
      />
    );
  }

  return (
    <div className="p-4 space-y-5">

      {/* New case + Compliance obligations — side by side, 2 wide */}
      <div className="flex items-center gap-2">
        {/* New case button — service providers only */}
        {isServiceProvider && (
          <button
            type="button"
            onClick={() => setMode("newcase")}
            disabled={loading}
            className="flex flex-1 items-center justify-center gap-1.5
                       text-[11px] py-2 rounded border transition
                       bg-[#16f2d4]/15 border-[#16f2d4]/50 text-[#16f2d4]
                       hover:bg-[#16f2d4]/25
                       disabled:opacity-50 disabled:cursor-not-allowed"
            title="Create a new case — uploads KMZ, names the run, picks participants"
          >
            <Plus size={12} />
            New case
          </button>
        )}

        {/* Compliance obligations — derived cross-case report. Available to any
            role; the report is scoped server-side to the caller's cases. */}
        <button
          type="button"
          onClick={() => setObligationsOpen(true)}
          className="flex flex-1 items-center justify-center gap-1.5
                     text-[11px] py-2 rounded border transition
                     bg-slate-800/40 border-slate-700/60 text-slate-200
                     hover:bg-slate-800 hover:border-slate-600"
          title="Compliance obligations — outstanding remediation + recommended next observations"
        >
          <ClipboardList size={12} />
          Compliance obligations
        </button>
      </div>

      {/* Cases list */}
      <div>
        <div className="text-[9px] font-mono uppercase tracking-widest text-fg-secondary mb-2
                        flex items-center gap-1.5">
          <FolderOpen size={10} />
          Cases
        </div>

        {casesListLoading && (
          <div className="text-[10px] text-fg-secondary italic">Loading…</div>
        )}

        {casesListError && !casesListLoading && (
          <div className="text-[10px] text-red-400/80">{casesListError}</div>
        )}

        {!casesListLoading && !casesListError
          && Array.isArray(casesList) && casesList.length === 0 && (
          <div className="text-[10px] text-fg-secondary italic leading-relaxed">
            {isServiceProvider
              ? `No cases yet. Click "New case" to start one.`
              : "No cases yet."}
          </div>
        )}

        {Array.isArray(casesList) && casesList.length > 0 && (
          <div className="flex flex-col gap-1">
            {casesList.map((c) => (
              <button
                key={c.id}
                onClick={() => loadCase(c.id)}
                disabled={loading}
                title={`Open "${c.name}" — ${STAGE_LABELS[c.current_stage] ?? c.current_stage}`}
                className="text-left rounded border border-slate-800/60 bg-slate-800/30
                           hover:bg-slate-800 hover:border-slate-700 transition
                           px-2 py-1.5 disabled:opacity-50 disabled:cursor-wait"
              >
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className={`text-[8px] font-mono uppercase tracking-widest border rounded px-1 py-[1px]
                                    ${STAGE_TONES[c.current_stage] ?? "bg-slate-800 border-slate-700 text-slate-400"}`}>
                    {STAGE_LABELS[c.current_stage] ?? c.current_stage}
                  </span>
                  <span className="text-[11px] text-slate-200 truncate flex-1">{c.name}</span>
                </div>
                <div className="text-[9px] text-fg-secondary mt-0.5 flex items-center gap-2">
                  <span>{c.pad_count} {c.pad_count === 1 ? "pad" : "pads"}</span>
                  <span>·</span>
                  <span>{timeAgo(c.updated_at)}</span>
                  <span>·</span>
                  <span>You: {CASE_ROLE_SHORT[c.my_role] ?? c.my_role}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {obligationsOpen && (
        <ObligationsReport onClose={() => setObligationsOpen(false)} />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   New case form
   ───────────────────────────────────────────────────────────────────────── */

// Single radio option — short label + Info icon with hover/long-press tooltip.
function DetectModeRadio({ value, current, onChange, disabled, label, help }) {
  const isSelected = current === value;
  return (
    <label
      className={`inline-flex items-center gap-1.5 text-[11px]
                  ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
    >
      <input
        type="radio"
        name="detect-mode"
        value={value}
        checked={isSelected}
        onChange={() => onChange(value)}
        disabled={disabled}
        className="accent-[#16f2d4]"
      />
      <span className={isSelected ? "text-slate-100" : "text-slate-300"}>
        {label}
      </span>
      <Info
        size={11}
        className="text-fg-secondary hover:text-slate-300 transition cursor-help"
        title={help}
        aria-label={help}
      />
    </label>
  );
}

// Stable option id for a vision engine. provider+model uniquely identifies an
// allow-list entry (the label is display-only).
const visionKey = (e) => `${e.provider}::${e.model}`;
// An engine is selectable unless the backend marks it available:false (a
// placeholder like the DreamHammer OGVision entries). Missing flag ⇒ available,
// so an older endpoint payload degrades gracefully rather than greying all.
const isEngineAvailable = (e) => e?.available !== false;

export function NewCaseForm({ onCancel, loading, error, createCase, source = "kmz" }) {
  // Source-aware: "kmz" → upload a KMZ; "selection" → pads come from the map
  // builder's draft (useCaseBuilderStore). Everything else (name, autodetect,
  // participants) is collected identically for both doors.
  const isFromSelection = source === "selection";
  const navigate = useNavigate();
  const draftPads = useCaseBuilderStore((s) => s.pads);
  const resetDraft = useCaseBuilderStore((s) => s.reset);

  const [fileError, setFileError] = useState(null);
  const [fileName, setFileName] = useState("");
  const [kmzB64, setKmzB64] = useState("");
  const [name, setName] = useState("");
  // Tri-state UI affordance over the two underlying config flags.
  // 'all'      → autodetect_enabled=true,  auto_advance_on_complete=true
  // 'per_pad'  → autodetect_enabled=true,  auto_advance_on_complete=false (default)
  // 'off'      → autodetect_enabled=false, auto_advance_on_complete=false
  // Default is per_pad — lazy is safer cost-wise than cascade.
  const [detectMode, setDetectMode] = useState("per_pad");
  const [customerOrgId, setCustomerOrgId] = useState("");
  const [operatorOrgId, setOperatorOrgId] = useState("");
  const [customerUserId, setCustomerUserId] = useState("");
  const [operatorUserId, setOperatorUserId] = useState("");
  // Migration 111 / REM-317: one organisation as both owner and respondent.
  // A DECLARED fact, not inferred from the two org pickers happening to match —
  // the server refuses that inference for the same reason.
  const [sameTenant, setSameTenant] = useState(false);
  // Participant directory — fetched from the org-directory endpoints (replaces
  // the hardcoded demoOrgs list). Both role dropdowns draw from the same
  // assignable-orgs list (no org typing); users are fetched per selected org.
  const [orgs, setOrgs] = useState([]);
  const [customerUsers, setCustomerUsers] = useState([]);
  const [operatorUsers, setOperatorUsers] = useState([]);
  // Vision engine — allow-list fetched from the backend (single source of
  // truth). visionTouched gates the payload: only an actively-chosen engine is
  // written to cases.config.vision_engine; an untouched control submits no key
  // and the case falls back to the env default (also Claude Opus 4.8).
  const [visionEngines, setVisionEngines] = useState([]);
  const [visionEngineKey, setVisionEngineKey] = useState("");
  const [visionTouched, setVisionTouched] = useState(false);

  // Case creation is restricted to service-provider orgs (e.g. DH). Backend
  // POST /cases (and the directory endpoints) enforce the same gate.
  const isServiceProvider = useEffectiveOrg()?.is_service_provider === true;

  // Load assignable orgs once; default the two role selections.
  useEffect(() => {
    if (!isServiceProvider) return;
    let live = true;
    fetchWithAuth("/api/v1/orgs/assignable")
      .then((r) => (r.ok ? r.json() : { orgs: [] }))
      .then((d) => {
        if (!live) return;
        const list = d.orgs ?? [];
        setOrgs(list);
        setCustomerOrgId((prev) => prev || list[0]?.id || "");
        setOperatorOrgId((prev) => prev || list[1]?.id || list[0]?.id || "");
      })
      .catch((e) => { console.error("[ui/src/components/padrekko/qc/QcPicker.jsx] GET /api/v1/orgs/assignable failed (swallowed):", e); });
    return () => { live = false; };
  }, [isServiceProvider]);

  // Users for the selected customer / operator org.
  useEffect(() => {
    if (!customerOrgId) { setCustomerUsers([]); setCustomerUserId(""); return; }
    let live = true;
    fetchWithAuth(`/api/v1/orgs/${encodeURIComponent(customerOrgId)}/users`)
      .then((r) => (r.ok ? r.json() : { users: [] }))
      .then((d) => { if (!live) return; const us = d.users ?? []; setCustomerUsers(us); setCustomerUserId(us[0]?.id ?? ""); })
      .catch((e) => { console.error("[ui/src/components/padrekko/qc/QcPicker.jsx] GET /api/v1/orgs/:id/users (customer) failed (swallowed):", e); });
    return () => { live = false; };
  }, [customerOrgId]);
  useEffect(() => {
    if (!operatorOrgId) { setOperatorUsers([]); setOperatorUserId(""); return; }
    let live = true;
    fetchWithAuth(`/api/v1/orgs/${encodeURIComponent(operatorOrgId)}/users`)
      .then((r) => (r.ok ? r.json() : { users: [] }))
      .then((d) => { if (!live) return; const us = d.users ?? []; setOperatorUsers(us); setOperatorUserId(us[0]?.id ?? ""); })
      .catch((e) => { console.error("[ui/src/components/padrekko/qc/QcPicker.jsx] GET /api/v1/orgs/:id/users (operator) failed (swallowed):", e); });
    return () => { live = false; };
  }, [operatorOrgId]);

  // Vision-engine allow-list. Backend GET is the single source of truth; the
  // pulldown never hardcodes the list. Default the visible selection to the
  // first entry (Claude Opus 4.8) WITHOUT marking it touched.
  useEffect(() => {
    if (!isServiceProvider) return;
    let live = true;
    fetchWithAuth("/api/v1/padrekko/qc/vision-engines")
      .then((r) => (r.ok ? r.json() : { engines: [] }))
      .then((d) => {
        if (!live) return;
        const list = d.engines ?? [];
        setVisionEngines(list);
        // Default to the first AVAILABLE entry — never a disabled placeholder.
        const firstAvail = list.find(isEngineAvailable);
        setVisionEngineKey((prev) => prev || (firstAvail ? visionKey(firstAvail) : ""));
      })
      .catch((e) => { console.error("[ui/src/components/padrekko/qc/QcPicker.jsx] GET /api/v1/padrekko/qc/vision-engines failed (swallowed):", e); });
    return () => { live = false; };
  }, [isServiceProvider]);

  if (!isServiceProvider) return null;

  async function onFile(e) {
    setFileError(null);
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".kmz")) {
      setFileError("Expected a .kmz file");
      e.target.value = "";
      return;
    }
    try {
      const b64 = await fileToBase64(file);
      setKmzB64(b64);
      setFileName(file.name);
      // Auto-populate name from filename if user hasn't typed one yet.
      if (!name) setName(file.name.replace(/\.kmz$/i, ""));
    } catch (err) {
      setFileError(err?.message ?? String(err));
    } finally {
      e.target.value = "";
    }
  }

  async function handleSubmit() {
    if (loading) return;
    if (!name.trim()) {
      setFileError("Case name is required");
      return;
    }
    if (isFromSelection) {
      if (draftPads.length === 0) {
        setFileError("No pads in the draft — go back to the map to select some");
        return;
      }
    } else if (!kmzB64) {
      setFileError("Please upload a KMZ file");
      return;
    }
      if (!customerOrgId) {
        setFileError("A customer org is required");
        return;
      }
      if (!sameTenant && !operatorOrgId) {
        setFileError("Customer and operator orgs are required");
        return;
      }
      // Only when the box is UNTICKED. With it ticked the two orgs being the
      // same is the point, and the operator picker is not rendered at all.
      if (!sameTenant && customerOrgId === operatorOrgId) {
        setFileError(
          "Customer and Operator must be different orgs. A case where one organization is both is created with the same-tenant option.");
        return;
      }
      if (!customerUserId || (!sameTenant && !operatorUserId)) {
        setFileError(sameTenant ? "A customer user is required"
                                : "Customer and operator users are required");
        return;
      }
    setFileError(null);

    // Map the tri-state UI to the two underlying flags. Backend checks
    // autodetect_enabled and (separately) the SSE handler reads
    // auto_advance_on_complete. participant_users is NO LONGER set here — the
    // backend folds each role's user_id (carried on participants below) into
    // config.participant_users, so both doors persist the assignee the same way.
    const config = {
      autodetect_enabled:       detectMode !== "off",
      auto_advance_on_complete: detectMode === "all",
    };
    // Vision engine: write it only when the control is visible (detect on) AND
    // the user actively picked an engine. Otherwise omit the key so the case
    // falls back to the env default — don't persist a choice the user didn't make.
    if (detectMode !== "off" && visionTouched) {
      const chosen = visionEngines.find((e) => visionKey(e) === visionEngineKey);
      // Only an available engine is ever written — a placeholder can't be the
      // selection, but guard the payload regardless of how state got here.
      if (chosen && isEngineAvailable(chosen)) {
        config.vision_engine = { provider: chosen.provider, model: chosen.model };
      }
    }
    // Participants carry the per-role assignee user_id; createPadrekkoCase reads
    // it for config.participant_users (single source for both doors).
      // A same-tenant case sends ONE participant. The server adds the
      // dh_analyst row itself, so the roster still ends at two and
      // UNIQUE (case_id, org_id) is never in play.
      const participants = sameTenant
        ? [{ org_id: customerOrgId, role: "customer_analyst", user_id: customerUserId }]
        : [
            { org_id: customerOrgId, role: "customer_analyst", user_id: customerUserId },
            { org_id: operatorOrgId, role: "operator_analyst", user_id: operatorUserId },
          ];

    if (isFromSelection) {
      // Map door — POST /cases/from-selection with the draft pads. On success
      // clear the draft and deep-link the new case (ReclamationQcTab loads it).
      try {
        const data = await useQcStore.getState().createCaseFromSelection({
          name: name.trim(),
          config,
          participants,
            same_tenant: sameTenant,
          pads: draftPads,
        });
        const id = data?.case?.id;
        resetDraft();
        navigate({
          to: "/map/$tab",
          params: { tab: "padrekko" },
          search: id ? { caseId: id } : {},
        });
      } catch {
        // Error surfaced via the store's `error` slot (rendered below).
      }
      return;
    }

    // KMZ door — unchanged path. createCase populates the store; ReclamationQcTab
    // re-renders to QcInspector automatically. Failure sets the `error` slot.
    await createCase({ name: name.trim(), kmzB64, config, participants, sameTenant });
  }

  return (
    <div className="p-4 space-y-4">
      {/* Header with back */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="flex items-center gap-1 text-[10px] text-slate-400
                     hover:text-slate-200 transition disabled:opacity-50"
        >
          <ChevronLeft size={11} />
          Back
        </button>
        <div className="text-[9px] font-mono uppercase tracking-widest text-fg-secondary">
          New case
        </div>
      </div>

      {/* Pad AOIs — source-aware. KMZ door uploads a file; map door shows the
          builder's draft pad count + an "Edit on map" link back to the builder
          (the draft survives the round-trip via useCaseBuilderStore). */}
      <div>
        <div className="text-[9px] font-mono uppercase tracking-widest text-fg-secondary mb-1.5">
          {isFromSelection ? "Pad AOIs (map selection)" : "Pad AOIs (KMZ)"}
        </div>
        {isFromSelection ? (
          <div className="flex items-center justify-between gap-2 w-full text-[11px] py-2 px-2.5 rounded border bg-slate-800/40 border-slate-700/60">
            <span className="flex items-center gap-1.5 text-slate-200">
              <MapPin size={12} className="text-accent-action" />
              {draftPads.length} {draftPads.length === 1 ? "pad" : "pads"} from map selection
            </span>
            <button
              type="button"
              onClick={() =>
                navigate({
                  to: "/map/$tab",
                  params: { tab: "explore" },
                  search: { mapMode: "padrekko_builder", returnTo: "padrekko" },
                })
              }
              disabled={loading}
              className="flex items-center gap-1 text-[11px] text-accent-action hover:brightness-110 transition disabled:opacity-50"
            >
              <Pencil size={11} /> Edit on map
            </button>
          </div>
        ) : (
          <label
            className={`flex items-center justify-center gap-1.5 w-full
                        text-[11px] py-2 rounded border transition cursor-pointer
                        ${loading
                          ? "bg-slate-800/40 border-slate-700/60 text-slate-500 cursor-wait"
                          : kmzB64
                            ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/15"
                            : "bg-slate-800/40 border-slate-700/60 text-slate-200 hover:bg-slate-800 hover:border-slate-600"}`}
            title={kmzB64 ? `Loaded: ${fileName}` : "Upload a KMZ of pad AOIs"}
          >
            <Upload size={12} />
            {kmzB64 ? `Loaded · ${fileName}` : "Upload KMZ"}
            <input
              type="file"
              accept=".kmz,application/vnd.google-earth.kmz"
              onChange={onFile}
              disabled={loading}
              className="hidden"
            />
          </label>
        )}
      </div>

      {/* Case name */}
      <div>
        <div className="text-[9px] font-mono uppercase tracking-widest text-fg-secondary mb-1.5">
          Case name
        </div>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={loading}
          placeholder="e.g. Permian Q2 close-out"
          className="w-full text-[11px] bg-slate-800/40 border border-slate-700/60
                     rounded px-2 py-1.5 text-slate-100
                     placeholder:text-slate-600
                     focus:outline-none focus:border-[#16f2d4]/50
                     disabled:opacity-50"
        />
      </div>

      {/* Autodetect mode — compact horizontal radio group. Wraps to
          stacked vertical on narrow panel widths via flex-wrap. */}
      <fieldset>
        <legend className="text-[9px] font-mono uppercase tracking-widest text-fg-secondary mb-1.5">
          Autodetect
        </legend>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
          <DetectModeRadio
            value="all"
            current={detectMode}
            onChange={setDetectMode}
            disabled={loading}
            label="All pads"
            help="Scan every pad in the case as soon as you open it."
          />
          <DetectModeRadio
            value="per_pad"
            current={detectMode}
            onChange={setDetectMode}
            disabled={loading}
            label="Per pad"
            help="Scan a pad when you navigate to it. Saves cost on pads you don't review."
          />
          <DetectModeRadio
            value="off"
            current={detectMode}
            onChange={setDetectMode}
            disabled={loading}
            label="Off"
            help="Manual review only. You draw your own polygons."
          />
        </div>
      </fieldset>

      {/* Vision model — governs the WHOLE case (autodetect detection AND
          operator-review calls). Shown only when autodetect is on; unmounts on
          "off". Options come from GET /vision-engines (no hardcoded copy). */}
      {detectMode !== "off" && visionEngines.length > 0 && (
        <div>
          <div className="text-[9px] font-mono uppercase tracking-widest text-fg-secondary mb-1.5">
            Vision model (detection &amp; review)
          </div>
          <select
            value={visionEngineKey}
            onChange={(e) => {
              const key = e.target.value;
              const picked = visionEngines.find((x) => visionKey(x) === key);
              if (picked && !isEngineAvailable(picked)) return; // never select a placeholder
              setVisionEngineKey(key);
              setVisionTouched(true);
            }}
            disabled={loading}
            className="w-full text-[11px] bg-slate-800/40 border border-slate-700/60
                       rounded px-2 py-1.5 text-slate-100 [color-scheme:dark]
                       focus:outline-none focus:border-[#16f2d4]/50
                       disabled:opacity-50"
          >
            {visionEngines.map((eng) => {
              const avail = isEngineAvailable(eng);
              return (
                <option
                  key={visionKey(eng)}
                  value={visionKey(eng)}
                  disabled={!avail}
                  className={avail ? "bg-slate-800 text-slate-100" : "bg-slate-800 text-slate-500"}
                >
                  {avail ? eng.label : `${eng.label} — Coming soon`}
                </option>
              );
            })}
          </select>
        </div>
      )}

{/* ── THE SAME-TENANT CONTROL (migration 111 / REM-317) ──────────────
          One organization as both owner and respondent. Ticking it is a
          DECLARATION, not an inference: the server refuses to read same-tenant
          from two pickers that happen to match, because "one participant" and
          "the operator org was deleted" would otherwise be the same fact. */}
      <label className="flex items-start gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={sameTenant}
          disabled={loading}
          onChange={(e) => { setSameTenant(e.target.checked); setFileError(null); }}
          className="mt-0.5 accent-accent"
        />
        <span className="text-[10px] leading-relaxed text-fg-secondary">
          <span className="text-fg">Same organization answers</span>
          <br />
          The customer is also the respondent — one org, both roles. The operator
          picker is hidden and the case is handed over by sharing it with
          themselves.
        </span>
      </label>

      {/* Customer + Operator participant pickers */}
      <div>
        <div className="text-[9px] font-mono uppercase tracking-widest text-fg-secondary mb-1.5">
          Participants
        </div>
        <div className="space-y-2.5">
          <div className="space-y-1">
            <Picker
              label={`${CASE_ROLE_LABEL.customer_analyst} — Org`}
              options={orgs.map((o) => ({ id: o.id, label: o.name }))}
              value={customerOrgId}
              onChange={setCustomerOrgId}
              disabled={loading}
            />
            <Picker
              label="User"
              options={customerUsers.map((u) => ({ id: u.id, label: u.name }))}
              value={customerUserId}
              onChange={setCustomerUserId}
              disabled={loading}
            />
          </div>
          {!sameTenant && (
            <div className="space-y-1">
            <Picker
              label={`${CASE_ROLE_LABEL.operator_analyst} — Org`}
              options={orgs.map((o) => ({ id: o.id, label: o.name }))}
              value={operatorOrgId}
              onChange={setOperatorOrgId}
              disabled={loading}
            />
            <Picker
              label="User"
              options={operatorUsers.map((u) => ({ id: u.id, label: u.name }))}
              value={operatorUserId}
              onChange={setOperatorUserId}
              disabled={loading}
            />
          </div>
          )}
        </div>
      </div>

      {/* Error surface */}
      {(fileError || error) && !loading && (
        <div className="text-[10px] text-red-400/80 leading-relaxed">
          {fileError || error}
        </div>
      )}

      {/* Submit */}
      <div className="pt-1">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={loading || !name.trim() || (isFromSelection ? draftPads.length === 0 : !kmzB64)}
          className="flex items-center justify-center gap-1.5 w-full
                     text-[11px] py-2 rounded border transition
                     bg-[#16f2d4]/15 border-[#16f2d4]/50 text-[#16f2d4]
                     hover:bg-[#16f2d4]/25
                     disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <ChevronRight size={12} />}
          {loading ? "Creating case…" : "Create case"}
        </button>
      </div>
    </div>
  );
}

// Generic labelled <select>. options: [{ id, label }].
function Picker({ label, options, value, onChange, disabled }) {
  return (
    <div>
      <div className="text-[9px] text-fg-secondary mb-0.5">{label}</div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled || options.length === 0}
        className="w-full text-[11px] bg-slate-800/40 border border-slate-700/60
                   rounded px-2 py-1.5 text-slate-100 [color-scheme:dark]
                   focus:outline-none focus:border-[#16f2d4]/50
                   disabled:opacity-50"
      >
        {options.length === 0 && (
          <option value="" className="bg-slate-800 text-slate-100">None available</option>
        )}
        {options.map((o) => (
          <option key={o.id} value={o.id} className="bg-slate-800 text-slate-100">
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}
